import _ from 'lodash';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import createReactClass from 'create-react-class';
import classnames from 'classnames';
import { TerraDraw, TerraDrawRectangleMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

// Unique map element id counter so multiple instances can coexist on the same page
let mapInstanceCounter = 0;

// Convert app-format fences to terra-draw GeoJSON features
function fencesToFeatures(fences) {
    return fences.map(fence => {
        // geobox — axis-aligned rectangle
        const { N, S, E, W } = fence;
        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[[W, N], [E, N], [E, S], [W, S], [W, N]]],
            },
            properties: { mode: 'rectangle' },
        };
    });
}

// Convert terra-draw snapshot features back to app-format fences
function featuresToFences(features) {
    return features
        .filter(f => f.properties.mode === 'rectangle')
        .map(f => {
            // rectangle
            const coords = f.geometry.coordinates[0];
            const lats = coords.map(c => c[1]);
            const lngs = coords.map(c => c[0]);
            return {
                type: 'geobox',
                N: Math.max(...lats),
                S: Math.min(...lats),
                E: Math.max(...lngs),
                W: Math.min(...lngs),
            };
        });
}

export default createReactClass({
    displayName: 'geofence-selector',

    propTypes: {
        onChange: PropTypes.func.isRequired,
        value: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.shape({
                    type: PropTypes.oneOf(['geobox']),
                    N: PropTypes.number.isRequired,
                    S: PropTypes.number.isRequired,
                    E: PropTypes.number.isRequired,
                    W: PropTypes.number.isRequired,
                }),
            ])
        ).isRequired,
        defaultZoom: PropTypes.number,
        defaultCenter: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }),
    },

    getDefaultProps() {
        return {
            value: [],
            defaultZoom: 12,
            defaultCenter: { lat: 43.6525, lng: -79.381667 }, // Toronto
        };
    },

    getInitialState() {
        return {
            selectedFeatureId: null,
            drawMode: 'static', // 'static' | 'rectangle'
        };
    },

    componentDidMount() {
        // Each map element needs a unique id for the terra-draw Google Maps adapter
        this._mapId = `ef3-geofenceSelector-map-${++mapInstanceCounter}`;
        this.refs.map.id = this._mapId;

        this.map = new google.maps.Map(this.refs.map, {
            center: this.props.defaultCenter,
            zoom: this.props.defaultZoom,
            scrollwheel: false,
        });

        // Build the list of active drawing modes
        const modes = [
            new TerraDrawSelectMode({
                flags: {
                    rectangle: {
                        feature: {
                            draggable: true,
                            coordinates: { resizable: 'opposite-vertices' },
                        },
                    },
                },
            }),
        ];
        modes.push(new TerraDrawRectangleMode());

        this.draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({ map: this.map, lib: google.maps }),
            modes,
        });

        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            // Wait for google maps to be ready and start to draw
            const state = this.draw.getModeState();
            if (state === 'stopped' || state === 'registered') {
                this.draw.start();
            }

            if (this.props.value && this.props.value.length) {
                this.draw.addFeatures(fencesToFeatures(this.props.value));
                this.fitBoundsToShapes();
                this.draw.setMode('select');
                this.setState({ drawMode: 'static' });
            }
        });

        // After a shape is drawn, switch back to idle (select) mode and notify parent
        this.draw.on('finish', (id, context) => {
            if (context.action === 'draw') {
                this.draw.setMode('select');
                this.setState({ drawMode: 'static' });
                this.onShapeUpdate();
            }
        });

        this.draw.on('select', id => {
            this.setState({ selectedFeatureId: id });
        });

        this.draw.on('deselect', () => {
            this.setState({ selectedFeatureId: null });
        });

        // Initialize searchbox
        this.searchResultMarkers = [];
        this.searchBox = new google.maps.places.SearchBox(this.refs.searchBox);
        google.maps.event.addListener(this.searchBox, 'places_changed', () => {
            // Clear search box
            this.refs.searchBox.value = '';

            // Clear previous markers
            this.searchResultMarkers.forEach(marker => marker.setMap(null));

            // Get new places
            const places = this.searchBox.getPlaces();
            if (!places.length) {
                return;
            }

            // Draw markers for each search result
            // There may be multiple if user is making a generic search
            // e.g. 'Pizza near Toronto, ON'
            places.forEach(place => {
                const marker = new google.maps.Marker({
                    position: place.geometry.location,
                    map: this.map,
                });
                this.searchResultMarkers.push(marker);
            });

            // Ensure all new places are visible on map
            const bounds = new google.maps.LatLngBounds();
            places.forEach(place => bounds.extend(place.geometry.location));
            this.map.fitBounds(bounds);
            // Prevent getting too close
            if (this.map.getZoom() > 12) {
                this.map.setZoom(12);
            }
        });
    },

    componentWillUnmount() {
        // ATTENTION:
        // https://code.google.com/p/gmaps-api-issues/issues/detail?id=3803
        // There is no way official way to clean up a Google Map
        // Best you can do is remove the event listeners that you manually added
        //
        // TODO in the far off future:
        // Figure out how to re-initialize and re-use existing Map instances
        if (this.draw) {
            try {
                this.draw.stop();
            } catch (error) {
                console.log('Error stopping draw:', error);
            }
        }
        google.maps.event.clearListeners(this.searchBox);
        this.searchResultMarkers.forEach(marker => marker.setMap(null));
    },

    shouldComponentUpdate(nextProps, nextState) {
        if (nextProps.value !== this.props.value) {
            if (this.draw && this.draw.enabled) {
                this.draw.clear();
                if (nextProps.value && nextProps.value.length) {
                    this.draw.addFeatures(fencesToFeatures(nextProps.value));
                }
            }
        }
        // Re-render when draw mode or selection changes (for button active state)
        return (
            nextState.drawMode !== this.state.drawMode ||
            nextState.selectedFeatureId !== this.state.selectedFeatureId
        );
    },

    onClickDrawRectangle() {
        const state = this.draw.getModeState();
        if (state === 'stopped' || state === 'registered') {
            this.draw.start();
        }

        this.draw.setMode('rectangle');
        this.setState({ drawMode: 'rectangle' });
    },

    onClickDeleteShape() {
        if (this.state.selectedFeatureId) {
            this.draw.removeFeatures([this.state.selectedFeatureId]);
            this.setState({ selectedFeatureId: null });
            this.onShapeUpdate();
        }
    },

    onClickDeleteAllShapes() {
        this.draw.clear();
        this.setState({ selectedFeatureId: null });
        this.onShapeUpdate();
    },

    fitBoundsToShapes() {
        if (!this.draw || !this.draw.enabled) {
            return;
        }
        const features = this.draw.getSnapshot().filter(f => f.properties.mode === 'rectangle');
        if (!features.length) {
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        features.forEach(f => {
            f.geometry.coordinates[0].forEach(([lng, lat]) => {
                bounds.extend({ lat, lng });
            });
        });
        this.map.fitBounds(bounds);
    },

    onShapeUpdate: _.debounce(function() {
        if (!this.draw || !this.draw.enabled) {
            return;
        }
        const fences = featuresToFences(this.draw.getSnapshot());
        this.props.onChange(fences);
    }, 300),

    render() {
        const rootClass = classnames('ef3-geofenceSelector', this.props.className);
        const { drawMode, selectedFeatureId } = this.state;

        return (
            <div ref="widget" className={rootClass}>
                <input ref="searchBox" className="ef3-geofenceSelector_searchBox" />
                <div ref="map" className="ef3-geofenceSelector_map" />
                <div className="ef3-geofenceSelector_toolbar">
                    <button
                        onClick={this.onClickDrawRectangle}
                        className={drawMode === 'rectangle' ? 'is-active' : ''}
                    >
                        Draw Rectangle
                    </button>
                    <button onClick={this.onClickDeleteShape} disabled={!selectedFeatureId}>
                        Remove selected fence
                    </button>
                    <button onClick={this.onClickDeleteAllShapes}>Remove all fences</button>
                    <button onClick={this.fitBoundsToShapes}>See all fences</button>
                </div>
            </div>
        );
    },
});
